import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { cn } from '@/lib/utils'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

/**
 * `line` for ordinary rows, `subtotal` for bold sums, `total` for a section result shown in a
 * shaded band, and `grand` for the final amount between heavy rules.
 */
export type LedgerEmphasis = 'line' | 'subtotal' | 'total' | 'grand'
export type LedgerValueTone = 'default' | 'muted' | 'success' | 'warning' | 'destructive'
/** A status band that draws the eye to a match (`success`) or a difference. */
export type LedgerTone = 'success' | 'warning' | 'destructive'

/**
 * Receipt-style list: labels on the left, amounts or inputs in one right-aligned column so a
 * column of typed counts lines up with the computed totals around it.
 */
export function Ledger({ className, children, ...props }: ComponentPropsWithoutRef<'div'>) {
  return (
    <div data-slot="ledger" className={cn('@container/ledger flex flex-col', className)} {...props}>
      {children}
    </div>
  )
}

interface LedgerSectionProps {
  title?: ReactNode
  /** Thin rules between rows; consecutive indented sources stay together as one group. */
  divided?: boolean
  children: ReactNode
}

export function LedgerSection({ title, divided = false, children }: LedgerSectionProps) {
  return (
    <section data-slot="ledger-section" className="pt-2 first:pt-0">
      {title ? (
        <Typography as="h4" variant="eyebrow" className="pb-1">
          {title}
        </Typography>
      ) : null}
      <dl
        className={cn(
          // A rule above every row except an indented source that follows another source;
          // status bands carry their own outline, so no rule touches them.
          divided &&
            '[&>[data-slot=ledger-row]]:border-border/60 [&>[data-slot=ledger-row]:not([data-tone])+[data-indent=false]:not([data-tone])]:border-t [&>[data-indent=false]+[data-indent=true]]:border-t'
        )}
      >
        {children}
      </dl>
    </section>
  )
}

interface LedgerRowProps {
  label: ReactNode
  /** Points the label at an input rendered as the value. */
  labelFor?: string
  hint?: ReactNode
  value: ReactNode
  emphasis?: LedgerEmphasis
  /** Zero source amounts stay visible but quiet; the label keeps its normal tone. */
  muted?: boolean
  /** Sources that add up to the subtotal after them, set in and regular weight. */
  indent?: boolean
  valueTone?: LedgerValueTone
  /** Paints the row as a status band; its label and amount take the status color. */
  tone?: LedgerTone
  /** Short status beside the amount, such as "Balanced". */
  badge?: ReactNode
  /**
   * `auto` lets a row hold several inputs; its right edge still aligns with the column. In a
   * narrow ledger the value moves under its label so the inputs keep a usable width.
   */
  valueWidth?: 'default' | 'auto'
  /** Optional full-width content under the row, such as a helper action. */
  children?: ReactNode
}

// Totals use the large figure size so the amounts that decide the close stand out.
const VALUE_VARIANT = {
  line: 'bodySmall',
  subtotal: 'labelStrong',
  total: 'h4',
  grand: 'h4',
} as const

const TONE_BAND: Record<LedgerTone, string> = {
  success: 'bg-status-success-background text-status-success-foreground ring-status-success-border',
  warning: 'bg-status-warning-background text-status-warning-foreground ring-status-warning-border',
  destructive:
    'bg-status-destructive-background text-status-destructive-foreground ring-status-destructive-border',
}

export function LedgerRow({
  label,
  labelFor,
  hint,
  value,
  emphasis = 'line',
  muted = false,
  indent = false,
  valueTone = 'default',
  tone,
  badge,
  valueWidth = 'default',
  children,
}: LedgerRowProps) {
  const isStrong = emphasis !== 'line'
  // A toned subtotal (a short or over line) gets a larger amount than its plain neighbours.
  const valueVariant = tone && emphasis === 'subtotal' ? 'emphasis' : VALUE_VARIANT[emphasis]

  return (
    <div
      data-slot="ledger-row"
      data-emphasis={emphasis}
      data-indent={indent}
      data-tone={tone}
      className={cn(
        'grid items-center gap-x-6 gap-y-1 py-2',
        valueWidth === 'auto'
          ? 'grid-cols-1 @md/ledger:grid-cols-[minmax(0,1fr)_auto]'
          : 'grid-cols-[minmax(0,1fr)_auto]',
        (emphasis === 'total' || tone) && '-mx-3 rounded-md px-3',
        emphasis === 'total' && !tone && 'bg-muted/40',
        tone && cn('my-1 ring-1 ring-inset', TONE_BAND[tone]),
        emphasis === 'grand' && 'my-2 border-y-2 border-foreground/70 py-3'
      )}
    >
      <dt className={cn('flex min-w-0 items-center gap-3', indent && 'pl-4')}>
        <span className="min-w-0 flex-1">
          {labelFor ? (
            <Label htmlFor={labelFor} weight={isStrong ? 'semibold' : 'medium'}>
              {label}
            </Label>
          ) : (
            <Typography
              as="span"
              variant={isStrong ? 'labelStrong' : indent ? 'bodySmall' : 'label'}
              tone={tone ? 'inherit' : undefined}
              className="block"
            >
              {label}
            </Typography>
          )}
          {hint ? (
            <Typography
              as="span"
              variant="caption"
              tone={tone ? 'inherit' : undefined}
              className="block"
            >
              {hint}
            </Typography>
          ) : null}
        </span>
        {badge ? <span className="shrink-0">{badge}</span> : null}
      </dt>
      <dd
        className={cn(
          'text-right',
          valueWidth === 'default'
            ? 'w-36 justify-self-end @md/ledger:w-40'
            : 'justify-self-stretch @md/ledger:justify-self-end'
        )}
      >
        {typeof value === 'string' ? (
          <Typography
            as="span"
            variant={valueVariant}
            tone={tone ? 'inherit' : muted && valueTone === 'default' ? 'muted' : valueTone}
            className="tabular-nums"
          >
            {value}
          </Typography>
        ) : (
          value
        )}
      </dd>
      {children ? <div className="col-span-2">{children}</div> : null}
    </div>
  )
}
